import { ChangeDetectionStrategy, Component, inject, output } from "@angular/core";
import { DatePipe } from "@angular/common";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatDialog } from "@angular/material/dialog";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTooltip } from "@angular/material/tooltip";
import { AiChatSessionService } from "../../services/ai-chat-session.service";
import { ConfirmDeleteThreadDialogComponent } from "./confirm-delete-thread-dialog.component";
import type { ThreadInfo } from "../../model/thread.types";

/**
 * Scrollable list of conversation threads.
 * Intended for use inside the chat settings mat-menu.
 *
 * Emits `threadSelected` when the user picks a thread so the parent can close
 * the menu (clicks inside are stopped from propagating to prevent auto-close).
 */
@Component({
  selector: "lib-chat-thread-list",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, MatIconButton, MatIcon, MatProgressSpinner, MatTooltip],
  templateUrl: "./chat-thread-list.component.html",
  styleUrl: "./chat-thread-list.component.scss",
})
export class ChatThreadListComponent {
  protected readonly chatSession = inject(AiChatSessionService);

  private readonly dialog = inject(MatDialog);

  /** Emitted when the user selects a thread — parent should close the settings menu. */
  readonly threadSelected = output<void>();

  protected isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  protected onSelect(threadId: string): void {
    this.chatSession.switchThread(threadId);
    this.threadSelected.emit();
  }

  protected onDeleteClick(threadId: string, threadName: string, event: MouseEvent): void {
    event.stopPropagation();
    const ref = this.dialog.open(ConfirmDeleteThreadDialogComponent, {
      width: "360px",
      data: { threadName },
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.chatSession.deleteThread(threadId);
      }
    });
  }
}
